import {CheckSpinner} from '@github-ui/mergebox'
import {usePrefersReducedMotion} from '@github-ui/use-prefers-reduced-motion'
import {SkeletonAvatar} from '@primer/react/experimental'
import {clsx} from 'clsx'
import type {ReactNode} from 'react'

import styles from './ForYouItemContent.module.css'
import {ForYouStatusIcon} from './ForYouStatusIcon'
import type {CanonicalForYouItemProps, ForYouItemAction, ForYouItemTone, ForYouItemVisual} from './for-you-item.types'

const NO_ACTIONS: readonly ForYouItemAction[] = []

const classNameByTone: Record<ForYouItemTone, string> = {
  success: 'fgColor-success',
  attention: 'fgColor-attention',
  danger: 'fgColor-onEmphasis',
  neutral: 'fgColor-default',
  done: 'fgColor-done',
}

const backgroundByTone: Record<ForYouItemTone, string> = {
  success: 'var(--bgColor-success-muted)',
  attention: 'var(--bgColor-attention-muted)',
  danger: 'var(--bgColor-danger-emphasis)',
  neutral: 'var(--bgColor-neutral-muted)',
  done: 'var(--bgColor-done-muted)',
}

export function ForYouItemContent({
  itemId,
  visual,
  title,
  subText,
  actions = NO_ACTIONS,
  footer,
}: CanonicalForYouItemProps) {
  const reducedMotion = usePrefersReducedMotion()
  const hasSubText = !!subText
  const renderedActions = actions.filter(action => action.content)
  const hasActions = renderedActions.length > 0

  return (
    <li className={styles.Container} data-testid="for-you-item-row" data-item-id={itemId}>
      <div className={clsx(styles.Row, !hasSubText && styles.WithoutSubText, hasActions && styles.WithActions)}>
        <div className={styles.Visual} data-testid="for-you-item-visual">
          <ForYouVisual visual={visual} reducedMotion={reducedMotion} />
        </div>
        <div className={styles.Title} data-testid="for-you-item-title">
          {title}
        </div>
        {hasSubText ? (
          <div className={styles.SubText} data-testid="for-you-item-subtext">
            {subText}
          </div>
        ) : null}
        {hasActions ? (
          <div className={styles.Actions}>
            {renderedActions.map(action => (
              <div key={action.id}>{action.content}</div>
            ))}
          </div>
        ) : null}
      </div>
      {footer}
    </li>
  )
}

export function ForYouVisual({visual, reducedMotion}: {visual: ForYouItemVisual; reducedMotion: boolean}) {
  if (visual.kind === 'skeleton') return <SkeletonAvatar size={40} />
  if (visual.kind === 'icon') {
    return <ForYouStatusIcon icon={visual.icon} tone={visual.tone} glyphSize={visual.glyphSize} />
  }
  if (visual.kind === 'donut') return <DonutVisual visual={visual} />

  return <SpinnerVisual visual={visual} reducedMotion={reducedMotion} />
}

function DonutVisual({visual}: {visual: Extract<ForYouItemVisual, {kind: 'donut'}>}) {
  if (visual.total <= 0) {
    return (
      <TonedVisual tone={visual.tone}>
        <svg className={styles.Donut} viewBox="0 0 24 24" aria-hidden="true">
          <circle className={styles.DonutTrack} cx="12" cy="12" r="9" />
        </svg>
      </TonedVisual>
    )
  }

  const completed = Math.max(0, Math.min(visual.completed, visual.total))
  const circumference = 2 * Math.PI * 9

  return (
    <TonedVisual tone={visual.tone}>
      <svg
        className={styles.Donut}
        viewBox="0 0 24 24"
        role="progressbar"
        aria-label={visual.accessibleLabel}
        aria-valuemin={0}
        aria-valuemax={visual.total}
        aria-valuenow={completed}
      >
        <circle className={styles.DonutTrack} cx="12" cy="12" r="9" />
        <circle
          className={styles.DonutValue}
          cx="12"
          cy="12"
          r="9"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - completed / visual.total)}
        />
      </svg>
    </TonedVisual>
  )
}

function SpinnerVisual({
  visual,
  reducedMotion,
}: {
  visual: Extract<ForYouItemVisual, {kind: 'spinner'}>
  reducedMotion: boolean
}) {
  const animated = visual.animated !== false && !reducedMotion

  return (
    <TonedVisual tone={visual.tone}>
      <span
        className={styles.ItemSpinner}
        data-testid="for-you-item-spinner"
        data-animated={animated ? 'true' : 'false'}
        aria-hidden="true"
      >
        <span className={styles.SpinnerScale}>
          <CheckSpinner animated={animated} />
        </span>
      </span>
    </TonedVisual>
  )
}

function TonedVisual({tone, children}: {tone: ForYouItemTone; children: ReactNode}) {
  return (
    <span className={styles.VisualOuterCircle} style={{backgroundColor: backgroundByTone[tone]}}>
      <span className={clsx(styles.VisualContent, classNameByTone[tone])}>{children}</span>
    </span>
  )
}
